import { Injectable } from '@angular/core'
import { Router, UrlTree } from '@angular/router'

export type LoginReason = 'auth' | 'expired' | 'suspended'

/**
 * Remembers the page a user was trying to reach when they were sent to /login
 * (no session, or session expired) so they can be returned there after signing in.
 */
@Injectable({ providedIn: 'root' })
export class AuthRedirectService {
    private readonly storageKey = 'auth_return_url'

    constructor(private router: Router) {}

    /** Login route carrying the attempted url and why the user was sent there. */
    loginUrlTree(returnUrl: string, reason: LoginReason = 'auth'): UrlTree {
        const safe = this.sanitize(returnUrl)
        return this.router.createUrlTree(['/login'], {
            queryParams: safe ? { returnUrl: safe, reason } : { reason }
        })
    }

    /** Navigate to login remembering the current page (used when the session dies mid-use). */
    redirectToLogin(reason: LoginReason = 'expired'): void {
        if (this.isLoginUrl(this.router.url)) {
            return
        }
        this.router.navigateByUrl(this.loginUrlTree(this.router.url, reason))
    }

    /** Only same-app absolute paths; blocks open redirects and login loops. */
    sanitize(url: string | null | undefined): string | null {
        if (!url || !url.startsWith('/') || url.startsWith('//') || url.includes('\\')) {
            return null
        }
        return this.isLoginUrl(url) || url === '/' ? null : url
    }

    // The social login round-trip leaves the app, so keep the target across it.
    save(url: string | null): void {
        try {
            if (url) {
                sessionStorage.setItem(this.storageKey, url)
            } else {
                sessionStorage.removeItem(this.storageKey)
            }
        } catch { /* storage unavailable */ }
    }

    consume(): string | null {
        try {
            const url = sessionStorage.getItem(this.storageKey)
            sessionStorage.removeItem(this.storageKey)
            return this.sanitize(url)
        } catch {
            return null
        }
    }

    private isLoginUrl(url: string): boolean {
        return /^\/(login|admin\/login|workshop\/login)(\?|#|\/|$)/.test(url)
    }
}
